import { Injectable, inject } from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {HttpHeaderService} from './http-header.service';
import {LetterResponse} from '../model/letter-response';
import {Observable} from 'rxjs';
import {APP_CONFIG} from '../config/app-config.token';
import {AppConfig} from '../config/app-config.model';
import {TaxForm} from '../model/tax-form';
import {UnemploymentPaymentRequest} from '../model/unemployment-payment-request';
import {UnemploymentApplicationStatus} from '../model/unemployment-application-status';
import {ApplicationResponse} from '../model/application-response';
import {CapacitorHttpService} from './capacitor-http.service';

@Injectable({
  providedIn: 'root'
})
export class LettersService {
  private http = inject(HttpClient);
  private capacitorHttp = inject(CapacitorHttpService);
  private httpHeaderService = inject(HttpHeaderService);
  private config = inject<AppConfig>(APP_CONFIG);

  baseurl: string;

  constructor() {
    this.baseurl = this.config.apiGatewayUrl;
  }

  getTaxForm(inss: string, taxFormId: string, year: string): Observable<LetterResponse> {
    // The consumer API only accepts `year`; a `region` param used to be sent but was always ignored.
    const params = new HttpParams().append('year', year);

    return this.capacitorHttp.get<LetterResponse>( this.baseurl + `/members/${inss}/taxforms/${taxFormId}`,
      { headers: this.httpHeaderService.getHeaderOptions().headers, params: params});
  }

  getTaxFormList(inss: string, year: string): Observable<TaxForm[]> {
    return this.capacitorHttp.get<TaxForm[]>( this.baseurl + `/members/${inss}/taxforms`,
      { headers: this.httpHeaderService.getHeaderOptions().headers,
                params: {year: year}});
  }

  getAttestationOfUnemployment(inss: string, lid: string, monthYYYYMM: string, docRegion: string): Observable<LetterResponse> {
    return this.capacitorHttp.get<LetterResponse>(this.baseurl + `/members/${inss}/attestationOfUnemployment`,
      {headers: this.httpHeaderService.getHeaderOptions().headers,
        params: {lid : lid,
          month: monthYYYYMM,
          docRegion: docRegion}});
  }

  getAttestationOfUnemploymentIncome(inss: string, lid: string, monthYYYYMM: string, docRegion: string): Observable<LetterResponse> {
    return this.capacitorHttp.get<LetterResponse>(this.baseurl + `/members/${inss}/attestationOfUnemploymentIncome`,
      {headers: this.httpHeaderService.getHeaderOptions().headers,
        params: {lid : lid,
          month: monthYYYYMM,
          docRegion: docRegion}});
  }


  getAttestationOfUnemploymentAmountsAndDays(inss: string, lid: string, startMonthYYYYMM: string,
                                             endMonthYYYYMM: string, docRegion: string): Observable<LetterResponse> {
    return this.capacitorHttp.get<LetterResponse>(this.baseurl + `/members/${inss}/attestationOfAmountsAndDays`,
      {headers: this.httpHeaderService.getHeaderOptions().headers,
        params: {lid : lid,
          startMonth: startMonthYYYYMM,
          endMonth: endMonthYYYYMM,
          docRegion: docRegion}});
  }

  getPaymentRequestsOverview(inss: string, startDate: string, endDate: string ): Observable<UnemploymentPaymentRequest[]> {
    return this.capacitorHttp.get<UnemploymentPaymentRequest[]>(this.baseurl + `/members/${inss}/unemployment/unemploymentRequestsFiltered`,
      {headers: this.httpHeaderService.getHeaderOptions().headers,
                params: { startDate: startDate,
                          endDate: endDate}});
  }

  getPaymentRequests(inss: string, creationDate: string, docNumber: string, docRegion: string ): Observable<LetterResponse> {
    return this.capacitorHttp.get<LetterResponse>(this.baseurl + `/members/${inss}/unemployment/paymentRequests`,
      {headers: this.httpHeaderService.getHeaderOptions().headers,
        params: {creationDate: creationDate,
                 docNumber: docNumber,
                 docRegion: docRegion}});
  }

  getLastPaymentRequest(inss: string): Observable<UnemploymentPaymentRequest> {
    return this.capacitorHttp.get<UnemploymentPaymentRequest>(
      this.baseurl + `/members/${inss}/unemployment/paymentRequests/last`,
      this.httpHeaderService.getHeaderOptions());
  }

  getLastUnemploymentApplication(ssin: string, statuses: UnemploymentApplicationStatus[]): Observable<ApplicationResponse> {
    return this.capacitorHttp.get<ApplicationResponse>(
      this.baseurl + `/members/${ssin}/unemployment/applications/last?statuses=${statuses.join(',')}`,
      this.httpHeaderService.getHeaderOptions());
  }

  getAllUnemploymentApplications(ssin: string, statuses: UnemploymentApplicationStatus[]): Observable<ApplicationResponse[]> {
    return this.capacitorHttp.get<ApplicationResponse[]>(
      this.baseurl + `/members/${ssin}/unemployment/applications?statuses=${statuses.join(',')}`,
      this.httpHeaderService.getHeaderOptions());
  }

  // ========== METHODS FOR EXTERNAL USERS (using /me endpoints) ==========
  // These methods are for external users (members) who can only access their own data.
  // Tyk API Gateway rewrites /members/me to /members/{jwt.preferred_username}

  /**
   * Get tax form for authenticated external user
   * @param taxFormId Tax form ID
   * @param year Year
   * @returns Observable<LetterResponse>
   */
  getMyTaxForm(taxFormId: string, year: string): Observable<LetterResponse> {
    // The consumer API only accepts `year`; a `region` param used to be sent but was always ignored.
    const params = new HttpParams().append('year', year);

    return this.capacitorHttp.get<LetterResponse>(
      this.baseurl + `/members/me/taxforms/${taxFormId}`,
      { headers: this.httpHeaderService.getHeaderOptions().headers, params: params}
    );
  }

  /**
   * Get tax form list for authenticated external user
   * @param year Year
   * @returns Observable<TaxForm[]>
   */
  getMyTaxFormList(year: string): Observable<TaxForm[]> {
    return this.capacitorHttp.get<TaxForm[]>(
      this.baseurl + '/members/me/taxforms',
      { headers: this.httpHeaderService.getHeaderOptions().headers, params: {year: year}}
    );
  }

  /**
   * Get attestation of unemployment for authenticated external user
   * @param lid LID
   * @param monthYYYYMM Month in format YYYY-MM
   * @param docRegion Document region
   * @returns Observable<LetterResponse>
   */
  getMyAttestationOfUnemployment(lid: string, monthYYYYMM: string, docRegion: string): Observable<LetterResponse> {
    return this.capacitorHttp.get<LetterResponse>(
      this.baseurl + '/members/me/attestationOfUnemployment',
      {headers: this.httpHeaderService.getHeaderOptions().headers,
        params: {lid : lid,
          month: monthYYYYMM,
          docRegion: docRegion}}
    );
  }

  /**
   * Get attestation of unemployment income for authenticated external user
   * @param lid LID
   * @param monthYYYYMM Month in format YYYY-MM
   * @param docRegion Document region
   * @returns Observable<LetterResponse>
   */
  getMyAttestationOfUnemploymentIncome(lid: string, monthYYYYMM: string, docRegion: string): Observable<LetterResponse> {
    return this.capacitorHttp.get<LetterResponse>(
      this.baseurl + '/members/me/attestationOfUnemploymentIncome',
      {headers: this.httpHeaderService.getHeaderOptions().headers,
        params: {lid : lid,
          month: monthYYYYMM,
          docRegion: docRegion}}
    );
  }

  /**
   * Get attestation of unemployment amounts and days for authenticated external user
   * @param lid LID
   * @param startMonthYYYYMM Start month in format YYYY-MM
   * @param endMonthYYYYMM End month in format YYYY-MM
   * @param docRegion Document region
   * @returns Observable<LetterResponse>
   */
  getMyAttestationOfUnemploymentAmountsAndDays(lid: string, startMonthYYYYMM: string,
                                                endMonthYYYYMM: string, docRegion: string): Observable<LetterResponse> {
    return this.capacitorHttp.get<LetterResponse>(
      this.baseurl + '/members/me/attestationOfAmountsAndDays',
      {headers: this.httpHeaderService.getHeaderOptions().headers,
        params: {lid : lid,
          startMonth: startMonthYYYYMM,
          endMonth: endMonthYYYYMM,
          docRegion: docRegion}}
    );
  }

  /**
   * Get payment requests overview for authenticated external user
   * @param startDate Start date
   * @param endDate End date
   * @returns Observable<UnemploymentPaymentRequest[]>
   */
  getMyPaymentRequestsOverview(startDate: string, endDate: string): Observable<UnemploymentPaymentRequest[]> {
    return this.capacitorHttp.get<UnemploymentPaymentRequest[]>(
      this.baseurl + '/members/me/unemployment/unemploymentRequestsFiltered',
      {headers: this.httpHeaderService.getHeaderOptions().headers,
        params: { startDate: startDate, endDate: endDate}}
    );
  }

  /**
   * Get payment requests for authenticated external user
   * @param creationDate Creation date
   * @param docNumber Document number
   * @param docRegion Document region
   * @returns Observable<LetterResponse>
   */
  getMyPaymentRequests(creationDate: string, docNumber: string, docRegion: string): Observable<LetterResponse> {
    return this.capacitorHttp.get<LetterResponse>(
      this.baseurl + '/members/me/unemployment/paymentRequests',
      {headers: this.httpHeaderService.getHeaderOptions().headers,
        params: {creationDate: creationDate,
          docNumber: docNumber,
          docRegion: docRegion}}
    );
  }

  /**
   * Get last payment request for authenticated external user
   * @returns Observable<UnemploymentPaymentRequest>
   */
  getMyLastPaymentRequest(): Observable<UnemploymentPaymentRequest> {
    return this.capacitorHttp.get<UnemploymentPaymentRequest>(
      this.baseurl + '/members/me/unemployment/paymentRequests/last',
      this.httpHeaderService.getHeaderOptions()
    );
  }

  /**
   * Get last unemployment application for authenticated external user
   * @param statuses Array of unemployment application statuses
   * @returns Observable<ApplicationResponse>
   */
  getMyLastUnemploymentApplication(statuses: UnemploymentApplicationStatus[]): Observable<ApplicationResponse> {
    return this.capacitorHttp.get<ApplicationResponse>(
      this.baseurl + `/members/me/unemployment/applications/last?statuses=${statuses.join(',')}`,
      this.httpHeaderService.getHeaderOptions()
    );
  }

  /**
   * Get all unemployment applications for authenticated external user
   * @param statuses Array of unemployment application statuses
   * @returns Observable<ApplicationResponse[]>
   */
  getMyAllUnemploymentApplications(statuses: UnemploymentApplicationStatus[]): Observable<ApplicationResponse[]> {
    return this.capacitorHttp.get<ApplicationResponse[]>(
      this.baseurl + `/members/me/unemployment/applications?statuses=${statuses.join(',')}`,
      this.httpHeaderService.getHeaderOptions()
    );
  }
}
